import React, { useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import { SectionBase } from '../SectionBase';
import AddressInput from 'shared/components/AddressInput';

const SectionMultiAddress = ({
  title,
  subTitle,
  helpText,
  error,
  questionId,
  value,
  onChange,
  onSetError,
  guidanceText,
}) => {
  const rows = useMemo(() => (Array.isArray(value) ? value : [{}]), [value]);
  const onChangeAddressArray = useMemo(
    () =>
      Array.from({ length: rows.length }).map((_, index) => event => {
        const clone = [...rows];
        clone[index] = {
          address: event.target.value,
          percent: clone[index].percent,
        };
        onChange(questionId, clone);
      }),
    [onChange, questionId, rows],
  );
  const onLatLngArray = useMemo(
    () =>
      Array.from({ length: rows.length }).map((_, index) => latLng => {
        const clone = [...rows];
        clone[index] = {
          ...latLng,
          percent: clone[index].percent,
        };
        onChange(questionId, clone);
      }),
    [onChange, questionId, rows],
  );
  const onChangePercentArray = useMemo(
    () =>
      Array.from({ length: rows.length }).map((_, index) => event => {
        const clone = [...rows];
        clone[index] = {
          ...clone[index],
          percent: event.target.value && parseFloat(event.target.value),
        };
        onChange(questionId, clone);

        const sum = clone.reduce((all, { percent = 0 }) => all + percent, 0);
        if (sum === 100) {
          onSetError();
        }
      }),
    [onChange, onSetError, questionId, rows],
  );
  const handleAddNew = useCallback(() => {
    const last = rows[rows.length - 1];
    if (last.address) {
      const clone = [...rows];
      clone.push({});
      onChange(questionId, clone);
    }
  }, [onChange, questionId, rows]);
  const lastRow = useMemo(() => rows?.length && rows[rows.length - 1], [rows]);
  const allInvalid =
    error && rows.reduce((all, { percent }) => all + percent, 0) !== 100;
  useEffect(() => {
    if (typeof value !== 'string') {
      return;
    }

    onChange(questionId, JSON.parse(value.replace(/\'/g, '"')));
  }, [value, onChange]);

  return (
    <SectionBase {...{ title, subTitle, helpText, guidanceText, questionId }}>
      <Info>
        For example, if one location serves 25 youth out of a total of 100 youth
        served by the program, then enter 25% (25/100) for that location.
      </Info>
      <br />
      {rows.map(({ address, percent }, index) => {
        const invalid =
          allInvalid || parseFloat(percent) < 0 || parseFloat(percent) > 100;

        return (
          <InputRow key={index}>
            <div>
              Location:
              <br />
              <Location
                value={address}
                onChange={onChangeAddressArray[index]}
                onLatLng={onLatLngArray[index]}
              />
            </div>
            &nbsp;&nbsp;
            <div>
              Percent:
              <br />
              <Row>
                <Percent
                  value={percent}
                  {...{ invalid }}
                  onChange={onChangePercentArray[index]}
                />
                &nbsp;%
                {invalid && <ErrorIcon>!</ErrorIcon>}
              </Row>
            </div>
          </InputRow>
        );
      })}
      <AddLocation
        onClick={handleAddNew}
        disabled={!lastRow?.address || !lastRow?.percent}
      >
        Add a Location
      </AddLocation>
    </SectionBase>
  );
};

const Row = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
`;

const InputRow = styled(Row)`
  margin-bottom: 10px;
`;

const LocationContainer = styled.div`
  width: 300px;
  position: relative;
`;

const Location = props => (
  <LocationContainer>
    <AddressInput {...props} />
  </LocationContainer>
);

const AddLocation = styled(Button).attrs({
  variant: 'outlined',
})`
  max-width: 200px;
  text-transform: none !important;
  font-size: 16px !important;
  font-weight: bold !important;
`;

const Percent = styled(TextField)`
  max-width: 60px;
  input {
    ${({ invalid }) =>
      invalid ? 'background-color: #FCD6D8; color: #AF0000;' : ''}
  }
`;

const Info = styled.div`
  color: #566778;
  font-size: 16px;
  margin: 10px 0;
`;

const ErrorIcon = styled.div`
  margin-left: 32px;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: #fcd6d8;
  color: #af0000;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  line-height: 32px;
`;

export default SectionMultiAddress;
